import { DATE_FORMAT, formatShortISO, parse } from '@eneco/common/date';
import {
  DC_Domain_Models_Customers_Gender,
  DC_Domain_Models_General_Address,
  Products_Order_ProductOffering,
  RequestModels_Products_Order_ProductOrder,
} from '@monorepo-types/dc';

import { FlowConsumerContextEnergy as FlowContext } from '../../../context';
import { selectProductCombinationByDiscountCode } from '../../helpers';
import type { OfferResponse } from '../../types';

const salutationToGender: Record<string, DC_Domain_Models_Customers_Gender> = {
  dhr: 'male',
  neutral: 'neutral',
  mw: 'female',
};

export const getElectricityUsage = (flowContext: FlowContext): Record<string, string> => {
  return {
    ...(flowContext.usageElectricityHigh && {
      tariff: flowContext.usageElectricityHigh.toString(),
    }),
    ...(flowContext.usageElectricityLow && {
      lowTariff: flowContext.usageElectricityLow.toString(),
    }),
    ...(flowContext.solarPanelsOutput && {
      redeliveryTariff: flowContext.solarPanelsOutput.toString(),
    }),
  };
};

export const getGasUsage = (flowContext: FlowContext): Record<string, string> => {
  return {
    ...(flowContext.usageGas && {
      tariff: flowContext.usageGas.toString(),
    }),
  };
};

export const getWarmthUsage = (flowContext: FlowContext): Record<string, string> => {
  return {
    ...(flowContext.usageWarmth && {
      tariff: flowContext.usageWarmth.toString(),
    }),
  };
};

export const getWarmWaterUsage = (flowContext: FlowContext): Record<string, string> => {
  return {
    ...(flowContext.usageWater && {
      tariff: flowContext.usageWater.toString(),
    }),
  };
};

export const getDeliveryAddress = (flowContext: FlowContext): DC_Domain_Models_General_Address => {
  return {
    type: 'Delivery',
    postalCode: flowContext.postalCode,
    houseNumber: flowContext.houseNumber,
    houseNumberSuffix: flowContext.houseNumberSuffix,
    street: flowContext.street,
    city: flowContext.city,
    hasResidentialPurpose: flowContext.hasResidencePurpose,
  };
};

export const getProducts = (flowContext: FlowContext, offer: OfferResponse): Array<Products_Order_ProductOffering> => {
  const selectedProductCombination = selectProductCombinationByDiscountCode(
    offer?.productCombinations ?? [],
    flowContext.discountCode,
  );
  const { deliveryDate, isMoving, hasCrossSellRentalDevice } = flowContext;

  const startDate = deliveryDate && formatShortISO(parse(deliveryDate, DATE_FORMAT));
  const actionReason = isMoving ? 'MoveIn' : 'Switch';

  const products: Array<Products_Order_ProductOffering> = [];
  const getProductName = (marketingProductCode?: string | null) => {
    return offer?.products?.find(p => p.marketingProductCode === marketingProductCode)?.description;
  };

  selectedProductCombination?.products?.forEach(p => {
    //The funcionality on cashbacks will be done on the DC side
    const product: Products_Order_ProductOffering = {
      productType: p.productType ?? 'unknown',
      usage: {},
      startDate,
      marketingProductCode: p.marketingProductCode,
      code: p.marketingProductCode,
      action: 'Add',
      actionReason,
      productName: getProductName(p.marketingProductCode),
    };

    switch (p.productType) {
      case 'electricity':
        product.usage = getElectricityUsage(flowContext);
        break;

      case 'gas':
        product.usage = getGasUsage(flowContext);
        break;

      case 'warmthEkv':
      case 'warmth':
        product.usage = getWarmthUsage(flowContext);
        break;

      case 'tapwater':
        product.usage = getWarmWaterUsage(flowContext);
        break;
    }

    products.push(product);
  });

  // add cross-sell rental device to the list of the ordered products
  if (hasCrossSellRentalDevice) {
    const selectedRentalDeviceProductCombination = offer?.productCombinations?.find(p =>
      p.products?.some(product => product.productType === 'huurapparaat'),
    );

    if (selectedRentalDeviceProductCombination) {
      const product: Products_Order_ProductOffering = {
        productType: selectedRentalDeviceProductCombination?.products?.[0]?.productType ?? 'unknown',
        usage: {},
        startDate,
        marketingProductCode: selectedRentalDeviceProductCombination?.products?.[0]?.marketingProductCode,
        code: selectedRentalDeviceProductCombination?.products?.[0]?.marketingProductCode,
        action: 'Add',
        actionReason,
      };

      products.push(product);
    }
  }

  return products;
};

export const getSendProductOrderRequestBody = (
  flowContext: FlowContext,
  offersData: OfferResponse,
): RequestModels_Products_Order_ProductOrder => {
  const selectedProductCombination = selectProductCombinationByDiscountCode(
    offersData?.productCombinations ?? [],
    flowContext.discountCode,
  );

  const desiredAdvancedPaymentAmount = selectedProductCombination?.costDetails?.find(
    costDetail => costDetail?.type === 'monthlyCosts',
  )?.costs?.vatIncluded;

  const gender = flowContext.salutation ? (salutationToGender[flowContext.salutation] ?? 'unknown') : 'unknown';

  return {
    data: {
      productOffers: getProducts(flowContext, offersData),
      customer: {
        isNew: true,
        person: {
          firstName: flowContext.firstName ?? '',
          initials: flowContext.initials ?? '',
          surnamePreposition: flowContext.surnamePreposition,
          surname: flowContext.surname ?? '',
          gender: gender,
          dateOfBirth: flowContext.birthdate && formatShortISO(parse(flowContext.birthdate, DATE_FORMAT)),
        },
        contact: {
          address: {
            type: 'Residential',
            postalCode: flowContext.postalCode,
            houseNumber: flowContext.houseNumber,
            houseNumberSuffix: flowContext.houseNumberSuffix,
            street: flowContext.street,
            city: flowContext.city,
            hasResidentialPurpose: flowContext.hasResidencePurpose,
          },
          phoneNumbers: [flowContext.phoneNumber?.replace(/[^\d]/g, '') ?? ''],
          emailAddress: flowContext.emailAddress,
          contactPreferences: {
            contactByTelephone: Boolean(flowContext.C2),
            productOffersFirstParty: false,
            productOffersFirstPartySubscribed: Boolean(flowContext.C1),
            productOffersThirdParty: Boolean(flowContext.C3),
          },
        },
        bank: {
          number: flowContext.iban?.replace(/\s/g, ''),
          autoDebetConsent: true,
          paymentMethod: 'directDebit',
        },
      },
      deliveryAddress: getDeliveryAddress(flowContext),
      processingType: 'distantSelling',
      configuration: {
        marketingCampaignCode: offersData?.configuration?.marketingCampaignCode,
        actionCode: offersData?.configuration?.actionCode,
        promotionCode: selectedProductCombination?.discountCode,
        discountType: selectedProductCombination?.discountType ?? 'unknown',
        digimonksExternalId: offersData?.configuration?.digimonksExternalId,
        nbaContext: selectedProductCombination?.nbaContext,
      },
      isResidence: flowContext.hasResidencePurpose,
      referenceId: flowContext.basketId,

      ...(!selectedProductCombination?.products?.some(p => p.productType === 'warmth') && {
        desiredAdvancePaymentAmount: Math.round(Number(desiredAdvancedPaymentAmount)),
      }),
    },
  };
};
